import t from 'react-translate';
import { Fragment, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import * as humps from 'humps';

// redux
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { initiateAIRolePlayScenarioGeneration } from 'redux/actions/ai-role-play';
import { getCurrentUser } from 'redux/selectors/users';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { wrapThunkAction } from 'redux/utils';

import NvTextArea from 'shared/components/inputs/nv-text-area';
import PusherService from 'shared/services/pusher-service';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { ModalWorkflowContext } from 'lecture_pages/components/workflows/modal-workflow';
import NvIcon from 'shared/components/nv-icon';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import NvGradientBorderButton from 'shared/components/button/nv-gradient-border-button';
import { danger } from 'styles/global_defaults/colors';
import { AIConfigStep } from '.';
import AiGenerationPreview from './ai-scenario-generating-preview';

type SetUpBasicsProps = {
  setStep: Function;
  onCancel: Function;
};

enum AiScenarioGenerationStatus {
  IDLE = 'idle',
  INITIATED = 'initiated',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

const AI_ROLE_PLAY_SCENARIO_CREATION_EVENT = 'ai_role_play_creation_completed';
const AI_ROLE_PLAY_SCENARIO_CREATION_FAILED_EVENT = 'ai_role_play_creation_failed';

const SetUpBasics = (props: SetUpBasicsProps) => {
  const { setStep, onCancel } = props;
  const dispatch = useDispatch();

  const params = useLecturePageParams();
  const { id: courseId } = useSelector((state) => getCurrentCourse(state));
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const { anonymizedIdentifier } = useSelector(getCurrentUser);
  const { getValues, reset } = useFormContext();
  const { settings, mode } = useContext(ModalWorkflowContext);
  const [aiScenarioGenerationStatus, setAiScenarioGenerationStatus] = useState(AiScenarioGenerationStatus.IDLE);

  const isAiGenerationDisabled = !getValues('contextDescription')
    || aiScenarioGenerationStatus === AiScenarioGenerationStatus.INITIATED
    || aiScenarioGenerationStatus === AiScenarioGenerationStatus.PROCESSING;
  const isManualCreationDisabled = aiScenarioGenerationStatus === AiScenarioGenerationStatus.INITIATED;

  const handleNext = () => {
    setStep(AIConfigStep.SET_UP_INSTRUCTIONS);
    reset();
  };

  const handleAiGeneratedScenarioInjection = useCallback((generatedContent) => {
    const {
      title,
      description,
      context,
      learnerRole,
      aiCharacterObjections,
      learnerGoals,
    } = generatedContent;

    reset({
      ...getValues(),
      title,
      description,
      contextDescription: context,
      learnerRole,
      aiCharacterObjections,
      requiredAiLearnerGoal: learnerGoals[0],
      aiLearnerGoals: learnerGoals.slice(1),
    });
    setAiScenarioGenerationStatus(AiScenarioGenerationStatus.COMPLETED);
    setStep(AIConfigStep.SET_UP_INSTRUCTIONS);
  }, [reset, getValues, setStep]);

  useEffect(() => {
    const identifier = anonymizedIdentifier.substring(0, 10);
    const channel = PusherService.initializeUserCourseChannel(identifier, courseId);

    channel.bind(AI_ROLE_PLAY_SCENARIO_CREATION_EVENT, (pusherData) => {
      pusherData = humps.camelizeKeys(pusherData);
      handleAiGeneratedScenarioInjection(pusherData);
    });

    channel.bind(AI_ROLE_PLAY_SCENARIO_CREATION_FAILED_EVENT, () => {
      setAiScenarioGenerationStatus(AiScenarioGenerationStatus.FAILED);
      dispatch(addAlertMessage({
        type: AlertMessageType.WARNING,
        header: t.FORM.OOPS(),
        message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CANNOT_GENERATE_SCENARIO(),
        customColor: danger,
      }));
    });

    return () => {
      channel.unbind(AI_ROLE_PLAY_SCENARIO_CREATION_EVENT);
      channel.unbind(AI_ROLE_PLAY_SCENARIO_CREATION_FAILED_EVENT);
    };
  }, [anonymizedIdentifier, courseId, dispatch, handleAiGeneratedScenarioInjection]);

  const handleGenerateRolePlay = () => {
    setAiScenarioGenerationStatus(AiScenarioGenerationStatus.INITIATED);
    wrapThunkAction(
      dispatch(initiateAIRolePlayScenarioGeneration({
        contextDescription: getValues('contextDescription'),
        lecturePageId: params.lecturePageId,
        catalogId: params.catalogId,
      })),
    ).then(() => {
      setAiScenarioGenerationStatus(AiScenarioGenerationStatus.PROCESSING);
    }).catch(() => {
      setAiScenarioGenerationStatus(AiScenarioGenerationStatus.FAILED);
      dispatch(addAlertMessage({
        type: AlertMessageType.WARNING,
        header: t.FORM.OOPS(),
        message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CANNOT_GENERATE_SCENARIO(),
        customColor: danger,
      }));
    });
  };

  const handleCloseModal = () => {
    setAiScenarioGenerationStatus(AiScenarioGenerationStatus.IDLE);
    onCancel();
    reset();
  };

  return (
    <div>
      <div className='d-flex w-100'>
        <GradientAiIcon icon='gen-ai' size='small' className='pr-1' />
        <p className='mb-0 font-weight-bolder page-title-xs'>
          {settings.title(mode)}
        </p>
        <button
          type='button'
          onClick={handleCloseModal}
          className='border-0 bg-white d-flex ml-auto cursor-pointer'
          aria-label={t.FORM.CLOSE()}
        >
          <NvIcon size='smallest' icon='close' />
        </button>
      </div>
      {aiScenarioGenerationStatus === AiScenarioGenerationStatus.PROCESSING ? (
        <AiGenerationPreview />
      ) : (
        <Fragment>
          <p className='mb-0 mt-6 page-title text-center'>
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LETS_CREATE_ROLE_PLAY()}
          </p>
          <div className='mt-4 mx-4'>
            <p className='text-large-body mb-2 lh-25 mb-0 font-weight-normal'>
              {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.GENERATE_YOUR_ROLE_PLAY_CONTENT(courseAliases)}
            </p>
            <NvTextArea
              withForm
              name='contextDescription'
              className='situation-textarea mt-4'
              textareaClassName='min-h-lg lh-30 text-large-body'
              placeholder={t.FORM.TYPE_HERE()}
              showErrorIcon={false}
            />
            <div className='d-flex justify-content-center mt-6'>
              <NvGradientBorderButton
                onClick={handleGenerateRolePlay}
                className='align-items-center py-2 px-5'
                ariaLabel={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.GENERATE_ROLE_PLAY()}
                disabled={isAiGenerationDisabled}
              >
                {isAiGenerationDisabled
                  ? <NvIcon icon='gen-ai' size='small' className='pr-2 text-gray-4' />
                  : <GradientAiIcon icon='gen-ai' size='small' className='pr-2' />}
                <span className={`${isAiGenerationDisabled ? 'text-gray-4' : ''} text-regular bold`}>
                  {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.GENERATE_ROLE_PLAY()}
                </span>
              </NvGradientBorderButton>
            </div>
            <button
              type='button'
              className={`${isManualCreationDisabled ? 'text-gray-4' : 'text-primary'} bg-white border-0 d-flex mx-auto mt-3 cursor-pointer font-weight-bold text-center`}
              onClick={handleNext}
              disabled={isManualCreationDisabled}
            >
              {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.MANUALLY_CREATE()}
            </button>
          </div>
        </Fragment>
      )}
    </div>
  );
};

export default SetUpBasics;
